import React, {
  useEffect, useCallback, useState,
} from 'react';
import { DEFAULT_THEME_COLOR } from 'appenv';
import styled from 'styled-components';
import 'objectFitPolyfill';
import ReactHtmlParser from 'react-html-parser';
import { Link } from 'react-router-dom';
import { LocalizedProduct } from 'models/types/products';
import { useSelector } from 'react-redux';
import ChatroomIcon from 'components/icons/Chatroom';
import CompanyLink from 'components/product/CompanyLink';
import CompanyName from 'components/product/CompanyName';
import isChatroomDisabled from 'components/chat/utils/isChatroomDisabled';
import useProductBoothData from 'hooks/useProductBoothData';

const ChatLink = styled(CompanyLink)<{ themeColor: string }>`
  margin: 16px 0 8px;
  display: inline-flex;
  align-items: center;
  color: ${({ themeColor }) => (themeColor || DEFAULT_THEME_COLOR)}

  & svg {
    height: 20px !important;
    width: 20px !important;
  }

  @media (max-width: 959px) {
    justify-content: flex-start;
  }
`;

const ProductItemContainer = styled(Link)`
  min-height: 300px;
  width: 330px;
  padding: 16px;
  margin: 8px 16px;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background-color: #FFF;
  align-self: initial;
  text-decoration: none;
  color: inherit;

  @media (max-width: 360px) {
    max-width: 100%;
  }

  @media (min-width: 960px) {
    background-color: rgba(255, 255, 255, 0.9);
    transition: background-color 0.2s;

    &:hover {
      background-color: rgba(255, 255, 255, 1);
    }
  }
`;

const ProductImageContainer = styled.div`
  position: relative;
  height: 0;
  padding-top: 100%;
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  object-fit: contain;
`;

const ProductTagsContainer = styled.div`
  width: 100%;
`;

declare global {
  interface Window {
    objectFitPolyfill: () => unknown;
  }
}

const ProductTag = styled.div`
  border-radius: 8px;
  margin: 4px;
  font-size: 0.75em;
  color: #484848;
  height: 20px;
  width: 100%;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  font-weight: bold;
`;

const ProductSpacer = styled.div`
  display: flex;
  flex-grow: 1;
`;

const NoImageContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  font-weight: bold;
  font-size: 1.4rem;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
`;

const ProductItemTags = ({ tags }: { tags: string[] }) => (
  <ProductTagsContainer>
    <ProductTag>
      {ReactHtmlParser(tags.map((tag) => `#${tag}`).join(' &nbsp;&nbsp;'))}
    </ProductTag>
  </ProductTagsContainer>
);

const ProductItemChatLink = ({ boothId, productName }: {
  boothId: string;
  productName: string;
}) => {
  const { themeColor } = useSelector((s: any) => s.eventConfigs);
  const {
    localizedBoothName, openChatWindow, loading, error,
  } = useProductBoothData(boothId, productName);

  if (loading || error || !localizedBoothName) {
    return null;
  }

  return (
    <ChatLink href="#" onClick={openChatWindow} themeColor={themeColor}>
      <ChatroomIcon />
      <CompanyName>{localizedBoothName}</CompanyName>
    </ChatLink>
  );
};

const ProductItem = ({
  product: {
    id,
    image,
    name,
    boothId,
    tags = [],
    oneLiner,
  },
  className,
  hideChatLink = false,
}: {
  product: LocalizedProduct;
  className?: string;
  hideChatLink?: boolean;
}) => {
  useEffect(() => {
    window.objectFitPolyfill();
  });
  const [errorLoading, setErrorLoading] = useState(false);
  const { products: { disableListChat } = { disableListChat: false }, chatroomConfig } = useSelector((s: any) => s.eventConfigs);
  const showChatLink = !disableListChat && !isChatroomDisabled(chatroomConfig) && !hideChatLink;
  const setImageError = useCallback(() => setErrorLoading(true), []);

  return (
    <ProductItemContainer to={`/products/${id}`} className={className}>
      <ProductImageContainer>
        {(!errorLoading || !image?.previewImageUrl) ? (
          <ProductImage
            src={image?.previewImageUrl}
            data-object-fit="contain"
            data-object-position="center"
            onError={setImageError}
          />
        ) : (
          <NoImageContainer />
        )}
      </ProductImageContainer>
      <h4>{name}</h4>
      <div>{ReactHtmlParser(oneLiner)}</div>
      <ProductSpacer />
      <ProductItemTags tags={tags} />
      {showChatLink && <ProductItemChatLink boothId={boothId} productName={name} />}
    </ProductItemContainer>
  );
};

export default React.memo(ProductItem);
